// Unpublished Work © 2025 Deere & Company.

import * as setupActions from '../actions/setup-workboard';

function removeWorkItems(workItems, idsToRemove, property) {
    const removedIds = new Set(idsToRemove ?? []);

    if (removedIds.size === 0) {
        return workItems ?? {};
    }

    return Object.fromEntries(
        Object.entries(workItems ?? {}).filter(([, workItem]) =>
            !removedIds.has(workItem[property]))
    );
}

const DEFAULT_NAME_VALIDATION = {
    isTouched: false,
    isDuplicate: false,
    duplicateEntityType: null,
    forcedDuplicateName: null,
    forcedDuplicateDateTime: null
};

const DEFAULT_STATE = {
    loadingSetup: false,
    isCopy: false,
    isDraft: false,
    details: {
        loading: true,
        name: '',
        date: '',
        pinLocation: '',
        notes: ''
    },
    operators: {
        loading: true,
        teamMembers: [],
        selected: []
    },
    jobs: {
        loading: true,
        jobs: [],
        selectedJobs: []
    },
    cutsAndDirections: {
        loading: true,
        cutHistory: null,
        selectedMowingDirections: []
    },
    fleetAndEquipment: {
        equipment: [],
        fleetId: '',
        loading: true
    },
    creationFlow: '',
    workItems: {},
    jobsToRemove: [],
    operatorsToRemove: [],
    rowOrder: [],
    isEditing: false,
    workboardId: null,
    initialWorkboard: {},
    initialSelectedOperators: new Set(),
    initialSelectedJobs: new Set(),
    initialWorkItems: new Map(),
    isSaving: false,
    nameValidation: {
        ...DEFAULT_NAME_VALIDATION
    }
};

const workboardSetup = (state = DEFAULT_STATE, action) => {
    switch (action.type) {
        case setupActions.UPDATE_DETAILS_FIELD: {
            const {
                field,
                value
            } = action;
            const shouldResetNameValidation = field === 'name' || field === 'date';

            const newDetails = {
                ...state.details
            };

            newDetails[field] = value;

            return {
                ...state,
                details: {
                    ...newDetails
                },
                nameValidation: shouldResetNameValidation ? {
                    ...DEFAULT_NAME_VALIDATION
                } : state.nameValidation
            };
        }
        case setupActions.SET_LOADING_DETAILS:
            return {
                ...state,
                details: {
                    ...state.details,
                    loading: action.loading
                }
            };
        case setupActions.SET_LOADING_OPERATORS:
            return {
                ...state,
                operators: {
                    ...state.operators,
                    loading: true
                }
            };
        case setupActions.SET_TEAM_MEMBERS:
            return {
                ...state,
                operators: {
                    ...state.operators,
                    teamMembers: [...action.teamMembers],
                    loading: false
                }
            };
        case setupActions.SELECT_OPERATOR:
        case setupActions.DELETE_SELECTED_OPERATOR:
            return {
                ...state,
                operators: {
                    ...state.operators,
                    selected: [...action.newSelected]
                },
                workItems: removeWorkItems(state.workItems, action.idsToRemove, 'operatorId'),
                rowOrder: (state.rowOrder ?? []).filter((rowId) => !(action.idsToRemove ?? []).includes(rowId)),
                operatorsToRemove: []
            };
        case setupActions.SET_OPERATORS_TO_DELETE:
            return {
                ...state,
                operatorsToRemove: [...action.operators]
            };
        case setupActions.SET_LOADING_JOBS:
            return {
                ...state,
                jobs: {
                    ...state.jobs,
                    loading: action.loading
                }
            };
        case setupActions.SET_JOBS:
            return {
                ...state,
                jobs: {
                    ...state.jobs,
                    jobs: [...action.jobs]
                }
            };
        case setupActions.SET_SELECTED_JOBS:
            return {
                ...state,
                jobs: {
                    ...state.jobs,
                    selectedJobs: [...action.newSelected]
                },
                jobsToRemove: []
            };
        case setupActions.REMOVE_SELECTED_JOBS:
            return {
                ...state,
                jobs: {
                    ...state.jobs,
                    selectedJobs: [...action.newSelected]
                },
                workItems: Object.fromEntries(
                    Object.entries(removeWorkItems(state.workItems, action.idsToRemove, 'jobId'))
                        .filter(([, workItem]) => !(action.idsToRemove ?? []).includes(workItem.savedJobId))
                ),
                rowOrder: (state.rowOrder ?? []).filter((rowId) => !(action.idsToRemove ?? []).includes(rowId)),
                jobsToRemove: []
            };
        case setupActions.SET_JOBS_TO_REMOVE:
            return {
                ...state,
                jobsToRemove: [...action.jobs]
            };
        case setupActions.SET_CUT_HISTORY:
            return {
                ...state,
                cutsAndDirections: {
                    ...state.cutsAndDirections,
                    cutHistory: action.history,
                    loading: false
                }
            };
        case setupActions.SET_SELECTED_MOWING_PATTERNS:
            return {
                ...state,
                cutsAndDirections: {
                    ...state.cutsAndDirections,
                    selectedMowingDirections: [...action.mowingPatterns]
                }
            };
        case setupActions.RESET_SETUP_STATE:
            return {
                ...DEFAULT_STATE
            };
        case setupActions.SET_FLEET_EQUIPMENT:
            return {
                ...state,
                fleetAndEquipment: {
                    ...state.fleetAndEquipment,
                    equipment: [...action.equipment],
                    fleetId: action.id
                }
            };
        case setupActions.SET_LOADING_FLEET_EQUIPMENT:
            return {
                ...state,
                fleetAndEquipment: {
                    ...state.fleetAndEquipment,
                    loading: action.loading
                }
            };
        case setupActions.SET_WORK_ITEMS:
            return {
                ...state,
                workItems: {
                    ...action.workItems
                }
            };
        case setupActions.SET_ROW_ORDER:
            return {
                ...state,
                rowOrder: action.value
            };
        case setupActions.SET_LOADING_SETUP:
            return {
                ...state,
                loadingSetup: action.value
            };
        case setupActions.SET_IS_COPY:
            return {
                ...state,
                isCopy: action.value
            };
        case setupActions.SET_COPY_WORKBOARD_MODE:
            return {
                ...state,
                isCopy: true,
                isEditing: false,
                workboardId: null,
                nameValidation: {
                    ...DEFAULT_NAME_VALIDATION
                },
                jobs: {
                    ...state.jobs,
                    loading: false
                }
            };
        case setupActions.SET_INITIAL_STATE: {
            return {
                ...state,
                loadingSetup: false,
                isEditing: action.values.isEditing,
                isDraft: action.values.isDraft || false,
                workboardId: action.values.workboardId ?? state.workboardId,
                details: {
                    name: action.values.name ?? state.details.name,
                    date: action.values.date ?? state.details.date,
                    pinLocation: action.values.pinLocation ?? state.details.pinLocation,
                    notes: action.values.note ?? state.details.notes
                },
                operators: {
                    ...state.operators,
                    teamMembers: [...action.values?.teamMembers],
                    loading: false,
                    selected: action.values.selectedOperators
                },
                jobs: {
                    ...state.jobs,
                    selectedJobs: action.values.selectedJobs
                },
                cutsAndDirections: {
                    ...state.cutsAndDirections,
                    selectedMowingDirections: [...action.values?.mowingPatterns],
                    loading: false
                },
                workItems: {
                    ...action.values.workItems
                },
                creationFlow: action.values.creationFlow ?? '',
                rowOrder: action.values.rowOrder ? [...action.values.rowOrder] : [],
                initialWorkboard: action.values.initialWorkboard,
                initialSelectedOperators: action.values.initialSelectedOperators,
                initialSelectedJobs: action.values.initialSelectedJobs,
                initialWorkItems: action.values.initialWorkItems,
                nameValidation: {
                    ...DEFAULT_NAME_VALIDATION
                }
            };
        }
        case setupActions.SET_IS_SAVING:
            return {
                ...state,
                isSaving: action.value
            };
        case setupActions.SET_NAME_VALIDATION: {
            const nextNameValidation = {
                ...state.nameValidation,
                ...action.value
            };

            if (nextNameValidation.isTouched === state.nameValidation.isTouched &&
                nextNameValidation.isDuplicate === state.nameValidation.isDuplicate &&
                nextNameValidation.duplicateEntityType === state.nameValidation.duplicateEntityType &&
                nextNameValidation.forcedDuplicateName === state.nameValidation.forcedDuplicateName &&
                nextNameValidation.forcedDuplicateDateTime === state.nameValidation.forcedDuplicateDateTime) {
                return state;
            }

            return {
                ...state,
                nameValidation: nextNameValidation
            };
        }
        default:
            return {
                ...state
            };
    }
};

export default workboardSetup;
